import {
  createRootRoute,
  createRoute,
  Navigate,
  Outlet,
} from '@tanstack/react-router';
import { Layout } from './components/layout';
import { Dashboard } from './pages/dashboard';
import { AlertsPage } from './pages/alerts';
import { LoginPage } from './pages/login';
import { RegisterPage } from './pages/register';
import { VerifyEmailPage } from './pages/verify-email';
import { ResetPasswordPage } from './pages/reset-password';
import { AuthGuard } from './components/auth-guard';
import { useAuthStore } from './stores/auth-store';

// Redirect to dashboard if already authenticated
function RedirectIfAuthenticated({ children }: { readonly children: React.ReactNode }) {
  const user = useAuthStore((s) => s.user);
  const isLoading = useAuthStore((s) => s.isLoading);

  if (isLoading) return null;
  if (user) return <Navigate to="/" />;
  return <>{children}</>;
}

// Root route with layout
const rootRoute = createRootRoute({
  component: () => (
    <Layout>
      <Outlet />
    </Layout>
  ),
});

// Dashboard route (home) — protected
const dashboardRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/',
  component: () => (
    <AuthGuard>
      <Dashboard />
    </AuthGuard>
  ),
});

// Alerts route — protected
const alertsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/alerts',
  component: () => (
    <AuthGuard>
      <AlertsPage />
    </AuthGuard>
  ),
});

// Auth routes
const loginRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/login',
  component: () => (
    <RedirectIfAuthenticated>
      <LoginPage />
    </RedirectIfAuthenticated>
  ),
});

const registerRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/register',
  component: () => (
    <RedirectIfAuthenticated>
      <RegisterPage />
    </RedirectIfAuthenticated>
  ),
});

const verifyRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/verify',
  component: VerifyEmailPage,
});

const resetRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/reset',
  component: () => (
    <RedirectIfAuthenticated>
      <ResetPasswordPage />
    </RedirectIfAuthenticated>
  ),
});

// Create the route tree
export const routeTree = rootRoute.addChildren([
  dashboardRoute,
  alertsRoute,
  loginRoute,
  registerRoute,
  verifyRoute,
  resetRoute,
]);
